/**
 * Query-key factory for ROOST-CRM (cluck.us).
 *
 * One source of truth for every react-query cache key. Hand-writing array
 * keys at the call site is how caches silently fail to dedupe or invalidate
 * (a typo'd key is a brand-new cache entry). ALWAYS build keys from here.
 *
 * CONVENTION
 * ----------
 * - Keys are namespaced by domain: `profile`, `vip`, `offers`, `balances`...
 * - Each namespace exposes:
 *     - `all`            -> broadest key, e.g. ["profile"]. Invalidate this
 *                           to refetch everything in the namespace.
 *     - specific funcs   -> e.g. `transactions(filters)` -> ["profile",
 *                           "transactions", filters]. Filters/params are the
 *                           LAST element so they participate in the cache key
 *                           (different filters = different cache entry) and so
 *                           a partial-match invalidation of the parent prefix
 *                           clears all filter variants at once.
 *
 * INVALIDATION
 * ------------
 * react-query matches keys by PREFIX. So:
 *   queryClient.invalidateQueries({ queryKey: queryKeys.profile.all })
 * invalidates transactions(filters), gameHistory(filters), and any future
 * profile query in one call. Mutations use the narrowest prefix that covers
 * what they changed (see the mutation pattern in ./README.md).
 *
 * Keep params SERIALIZABLE and STABLE. react-query hashes keys structurally,
 * so object key ORDER does not matter, but values must be primitives / plain
 * objects (no functions, no class instances).
 */

export const queryKeys = {
  /** Player profile domain: transactions, game history. */
  profile: {
    all: ["profile"],
    me: () => ["profile", "me"],
    transactions: (filters) => ["profile", "transactions", filters],
    gameHistory: (filters) => ["profile", "gameHistory", filters],
  },

  /** VIP tiers + the player's VIP standing. */
  vip: {
    all: ["vip"],
    tiers: () => ["vip", "tiers"],
    status: (userId) => ["vip", "status", userId],
  },

  /** Offers / store, daily-claim status, purchase status. */
  offers: {
    all: ["offers"],
    list: () => ["offers", "list"],
    dailyStatus: (userId) => ["offers", "dailyStatus", userId],
    purchaseStatus: (userId) => ["offers", "purchaseStatus", userId],
  },

  /** Wallet balances (most sensitive - keyed by user). */
  balances: {
    all: ["balances"],
    detail: (userId) => ["balances", "detail", userId],
  },

  /** Redemption tracking. */
  redemptions: {
    all: ["redemptions"],
    detail: (transactionId) => ["redemptions", "detail", transactionId],
  },

  /**
   * Admin player-detail domain (PlayerDetailContent + its section components
   * under admin/components/player-detail/). Keyed by the admin's target
   * playerId, NOT the logged-in admin.
   */
  adminPlayers: {
    all: ["adminPlayers"],
    detail: (playerId) => ["adminPlayers", "detail", playerId],
    transactions: (playerId, filters) => ["adminPlayers", "transactions", playerId, filters],
    redemptions: (playerId, filters) => ["adminPlayers", "redemptions", playerId, filters],
    bonuses: (playerId) => ["adminPlayers", "bonuses", playerId],
    notes: (playerId) => ["adminPlayers", "notes", playerId],
    kyc: (playerId) => ["adminPlayers", "kyc", playerId],
    activity: (playerId, filters) => ["adminPlayers", "activity", playerId, filters],
    round: (playerId, roundId) => ["adminPlayers", "round", playerId, roundId],
  },

  /** Admin game catalog management (GameManagement). */
  adminGames: {
    all: ["adminGames"],
    list: (filters) => ["adminGames", "list", filters],
    detail: (gameId) => ["adminGames", "detail", gameId],
    providers: () => ["adminGames", "providers"],
    categories: () => ["adminGames", "categories"],
    features: () => ["adminGames", "features"],
  },

  /** Player-facing game catalog / lobby (GameLobby). */
  games: {
    all: ["games"],
    lobby: (filters) => ["games", "lobby", filters],
    category: (categoryId, filters) => ["games", "category", categoryId, filters],
    favorites: (userId) => ["games", "favorites", userId],
    recentlyPlayed: (userId) => ["games", "recentlyPlayed", userId],
    recommendations: (userId) => ["games", "recommendations", userId],
    search: (query) => ["games", "search", query],
    providers: () => ["games", "providers"],
    enabledGamesCount: () => ["games", "enabledGamesCount"],
    enabledProvidersCount: () => ["games", "enabledProvidersCount"],
    specialOffersAvailable: () => ["games", "specialOffersAvailable"],
  },
};

export default queryKeys;
